import React from "react";
import { motion } from "framer-motion";
import { ClipboardList, Sparkles, Stethoscope } from "lucide-react";

const columns = [
  {
    icon: ClipboardList,
    label: "Signals you submit",
    title: "What goes in",
    points: [
      "An about-a-minute morning check-in: energy, sleep quality, brain fog, stress, and mood on a 1–5 scale.",
      "Optional context tags you choose, heat, cycle phase, infection, workload, travel.",
      "Optional wearable data you connect, sleep duration, resting heart rate, HRV, and step count.",
    ],
    footnote: "You decide what to log. Every field is optional.",
  },
  {
    icon: Sparkles,
    label: "What the model estimates",
    title: "What comes out",
    points: [
      "A likely energy range for tomorrow and the next few days, compared to your own baseline.",
      "Windows where your history suggests focus, movement, or rest are more likely to land well.",
      "Repeating patterns across your tags, the conditions that tend to precede a good or hard day for you.",
    ],
    footnote: "Estimates are personal, probabilistic, and refine as you log more check-ins.",
  },
  {
    icon: Stethoscope,
    label: "What it is not",
    title: "Why it isn't medical advice",
    points: [
      "Myelina Health is a wellness and self-tracking tool, not a medical device or diagnostic system.",
      "It does not detect relapses, interpret symptoms, or make treatment recommendations.",
      "Nothing here replaces your neurologist, MS nurse, or care team, bring the patterns to them.",
    ],
    footnote: "If something feels urgent or new, contact your clinician.",
  },
];

const HowPredictionsWork = React.forwardRef<HTMLElement>((_, ref) => {
  return (
    <section
      ref={ref}
      id="how-predictions-work"
      aria-labelledby="how-predictions-heading"
      className="bg-secondary py-20 sm:py-28"
    >
      <div className="container mx-auto px-6 sm:px-10 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="text-center space-y-4 mb-14"
          >
            <div className="text-xs font-medium text-primary tracking-[0.22em] uppercase">
              How predictions are generated
            </div>
            <h2
              id="how-predictions-heading"
              className="font-display text-3xl sm:text-4xl text-foreground tracking-tight"
            >
              Your signals in. Your pattern out.
            </h2>
            <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
              Predictions are pattern recognition on the data you choose to share, not prophecy,
              and not a clinical assessment.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 md:gap-8">
            {columns.map((col, i) => (
              <motion.article
                key={col.title}
                initial={{ opacity: 0, y: 6 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="rounded-2xl border border-border bg-surface p-6 sm:p-8 shadow-sm flex flex-col"
              >
                <div className="mb-4 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary">
                  <col.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <div className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mb-2">
                  {col.label}
                </div>
                <h3 className="font-display text-xl text-foreground mb-4">
                  {col.title}
                </h3>
                <ul className="space-y-3 text-sm text-foreground/80 leading-relaxed">
                  {col.points.map((p) => (
                    <li key={p} className="flex gap-2">
                      <span
                        aria-hidden="true"
                        className="mt-2 w-1.5 h-1.5 rounded-full bg-primary/60 flex-shrink-0"
                      />
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
                <p className="mt-5 pt-4 border-t border-border/60 text-xs text-muted-foreground italic">
                  {col.footnote}
                </p>
              </motion.article>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-10 text-center text-xs text-muted-foreground max-w-2xl mx-auto"
          >
            Myelina Health is intended for general wellness and self-tracking. It is not a
            substitute for professional medical advice, diagnosis, or treatment. Always seek the
            guidance of your clinician with any questions about your health.
          </motion.p>

          <motion.aside
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.25 }}
            aria-labelledby="reading-heading"
            className="mt-12 pt-8 border-t border-border/60 max-w-2xl mx-auto"
          >
            <h3
              id="reading-heading"
              className="text-[11px] tracking-[0.22em] uppercase text-muted-foreground text-center mb-4"
            >
              What we're reading
            </h3>
            <ul className="space-y-2 text-xs text-muted-foreground leading-relaxed">
              <li>
                <a
                  href="https://www.nationalmssociety.org/about-multiple-sclerosis/what-is-ms/ms-prevalence"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-border hover:decoration-primary underline-offset-2"
                >
                  National MS Society | MS prevalence in the U.S.
                </a>{" "}, source for the ~1 million U.S. adults figure.
              </li>
              <li>
                <a
                  href="https://pubmed.ncbi.nlm.nih.gov/28729168/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-border hover:decoration-primary underline-offset-2"
                >
                  Kratz et al. (2017), Arch Phys Med Rehabil
                </a>{" "}, day-to-day variability of MS fatigue.
              </li>
              <li>
                <a
                  href="https://www.archives-pmr.org/article/S0003-9993(13)00112-3/abstract"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-border hover:decoration-primary underline-offset-2"
                >
                  Blikman et al., Arch Phys Med Rehabil
                </a>{" "}, energy conservation strategies, meta-analysis.
              </li>
              <li>
                <a
                  href="https://www.jmir.org/2025/1/e70871"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline decoration-border hover:decoration-primary underline-offset-2"
                >
                  Chen et al. (2025), JMIR
                </a>{" "}, digital phenotyping of MS.
              </li>
            </ul>
            <p className="mt-4 text-[11px] text-muted-foreground/80 italic text-center">
              The research we build on. We are not affiliated with these authors.
            </p>
          </motion.aside>
        </div>
      </div>
    </section>
  );
});

HowPredictionsWork.displayName = "HowPredictionsWork";

export default HowPredictionsWork;
